import { CalendarDays, CalendarPlus, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useGoogleAvailable } from "@/hooks/use-google-connection";
import { type PtoEntry, useDeletePtoEntry, usePtoEntries } from "@/hooks/use-pto-entries";
import { PTO_ENTRY_STATUS_OPTIONS } from "@/lib/enums";
import { formatDate } from "@/lib/format";
import { PtoEntryCreateDialog } from "./PtoEntryCreateDialog";
import { PtoEntryEditDialog } from "./PtoEntryEditDialog";
import { PtoEntrySendCalendarEventDialog } from "./PtoEntrySendCalendarEventDialog";

function dateRangeLabel(entry: PtoEntry): string {
  return entry.startDate === entry.endDate
    ? formatDate(entry.startDate)
    : `${formatDate(entry.startDate)} – ${formatDate(entry.endDate)}`;
}

/**
 * The PTO Entries data grid — mirrors WidgetsTable.tsx: `useDataGrid` owns
 * search/status/sort/cursor state, `usePtoEntries` is keyed on `grid.query`,
 * `<DataTable>` renders headers/loading/empty. Only `status` is filterable in
 * v1 (type is shown as a column, not yet a filter — see .claude/rules/frontend.md).
 */
export function PtoEntriesTable() {
  const grid = useDataGrid({ defaultSort: "startDate" });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PtoEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PtoEntry | null>(null);
  const [calendarTarget, setCalendarTarget] = useState<PtoEntry | null>(null);

  const { data, isLoading, isError, error, refetch } = usePtoEntries(grid.query, grid.pageRequest);
  const deleteEntry = useDeletePtoEntry();
  const googleAvailable = useGoogleAvailable();

  const entries = data?.data ?? [];

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteEntry.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search notes..."
        className="w-full md:flex-1 md:max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={PTO_ENTRY_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New entry
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load PTO entries"}
          onRetry={() => refetch()}
        />
        <PtoEntryCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Dates", sortKey: "startDate" },
          { label: "Type", sortKey: "type" },
          { label: "Status", sortKey: "status" },
          { label: "Hours", className: "text-right", sortKey: "totalHours" },
          { label: "Suggestion", className: "hidden lg:table-cell" },
          { label: "Note", className: "hidden md:table-cell" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={CalendarDays}
            title="No PTO entries found"
            description="Try adjusting your search or filters, or add a new entry."
          />
        }
      >
        {entries.map((entry) => (
          <tr
            key={entry.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{dateRangeLabel(entry)}</td>
            <td className="px-4 py-3 capitalize">{entry.type}</td>
            <td className="px-4 py-3">
              <StatusBadge status={entry.status} />
            </td>
            <td className="px-4 py-3 text-right">{entry.totalHours ?? "Full day"}</td>
            <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {entry.suggestionTitle ?? "—"}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {entry.note ?? "—"}
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  { label: "Edit", icon: Pencil, onSelect: () => setEditTarget(entry) },
                  ...(googleAvailable.data
                    ? [
                        {
                          label: "Send calendar invite",
                          icon: CalendarPlus,
                          onSelect: () => setCalendarTarget(entry),
                        },
                      ]
                    : []),
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(entry),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="entries"
      />

      <PtoEntryCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <PtoEntryEditDialog
        entry={editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      />
      <PtoEntrySendCalendarEventDialog
        entry={calendarTarget}
        onOpenChange={(open) => {
          if (!open) setCalendarTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete PTO entry"
        description={`Delete this entry (${deleteTarget ? dateRangeLabel(deleteTarget) : ""})?`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteEntry.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
